import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import type { Locale } from '@worldcoo/monolith/core';
import { useState, useEffect } from 'react';

/**
 * Animated counter that shows donations arriving at their real timestamps.
 *
 * Receives `totalAtWindowStartCents` (server-computed sum of ALL donations
 * before the current window) and an array of donations within the window.
 * Polls Date.now() and adds each donation whose timestamp has been reached,
 * so the counter animates up in real time.
 *
 * Formula: displayedTotal = totalAtWindowStartCents/100 + sum(arrived)/100
 *
 * When `donations` changes (new window), the counter continues smoothly
 * because the new `totalAtWindowStartCents` already includes all previous
 * window donations.
 */
export function AnimatedTotalRaised({
	totalAtWindowStartCents,
	donations,
	language,
}: {
	totalAtWindowStartCents: number;
	donations: { amountCents: number; timestamp: number }[];
	language: Locale;
}) {
	const [now, setNow] = useState(Date.now());

	useEffect(() => {
		if (donations.length === 0) return;
		const allDone = donations.every((d) => d.timestamp <= now);
		if (allDone) return;
		const timer = setInterval(() => setNow(Date.now()), 50);
		return () => clearInterval(timer);
	}, [donations, now]);

	const arrivedCents = donations.filter((d) => d.timestamp <= now).reduce((sum, d) => sum + d.amountCents, 0);
	const currentValue = (totalAtWindowStartCents + arrivedCents) / 100;

	return <AnimateNumber locale={language} value={currentValue} suffix="€" decimalDigits={2} />;
}
